import { lazy } from 'react';
/** One flag per chunk, so a genuinely broken page cannot reload forever. */
const RELOAD_FLAG = 'ps.chunk-reloaded';
function alreadyReloaded(key) {
    try {
        return sessionStorage.getItem(`${RELOAD_FLAG}:${key}`) === '1';
    }
    catch {
        // Private window, blocked storage. Without a flag a reload loop is
        // possible, so treat it as "already tried" and show the error instead.
        return true;
    }
}
function markReloaded(key) {
    try {
        sessionStorage.setItem(`${RELOAD_FLAG}:${key}`, '1');
    }
    catch {
        // Nothing to do; the branch above refuses the reload in this case anyway.
    }
}
/** Clears the flags once the app has loaded successfully. */
export function clearChunkReloadFlags() {
    try {
        for (const key of Object.keys(sessionStorage)) {
            if (key.startsWith(RELOAD_FLAG))
                sessionStorage.removeItem(key);
        }
    }
    catch {
        // Storage is unavailable; there is nothing to clear.
    }
}
/**
 * `React.lazy`, but it survives a deployment.
 *
 * A route's code is fetched when somebody first navigates to it. If the site
 * has been redeployed since the page was opened, that file no longer exists
 * under the name this build asked for, and the import fails with
 * "error loading dynamically imported module" — a dead end on a page that was
 * working a moment ago. The same thing happens in development whenever the
 * shared packages are rebuilt underneath a running dev server.
 *
 * Neither case is a broken page; both are a stale page. So the first failure
 * reloads once, which fetches the current build and lands the person where they
 * were going. A second failure for the same chunk is a real error and is left to
 * the error boundary, because reloading again would only loop.
 */
export function lazyWithRetry(key, load) {
    // Generic over the props so a route that takes some — `<PeoplePage role=… />`
    // — keeps its type. A loader that erased props would be checked as though
    // every page took none.
    return lazy(async () => {
        try {
            return await load();
        }
        catch (error) {
            if (alreadyReloaded(key))
                throw error;
            markReloaded(key);
            window.location.reload();
            // The reload is not instant. Returning a promise that never settles keeps
            // the Suspense fallback on screen rather than flashing an error first.
            return new Promise(() => { });
        }
    });
}
//# sourceMappingURL=lazyWithRetry.js.map